import { FC, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Form, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';
import { Box } from '@mui/material';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FormContainer } from '../common/form/FormContainer';
import { DetailHeader } from '../common/DetailHeader';
import { QcsTabs } from '../common/basic/QcsTabs';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { InfoPaper } from '../common/basic/InfoPaper';
import { ReadOnlyField } from '../common/basic/ReadOnlyField';
import QcsAlert from '../common/basic/QcsAlert';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { IntegrationStatusChip } from './IntegrationStatusChip';
import {
  IntegrationFormFields,
  integrationFormValidation,
  IntegrationFormValues,
  IntegrationSubmitButton,
  toApiClientUpdateDto,
  toIntegrationFormValues,
} from './IntegrationFormFields';
import { IntegrationKeys } from './IntegrationKeys';
import { IntegrationLogs } from './IntegrationLogs';
import { IntegrationSetActiveDialog } from './IntegrationSetActiveDialog';
import { IntegrationConnectionDetails } from './IntegrationConnectionDetails';
import { IntegrationKeyExpiry } from './IntegrationKeyExpiry';
import {
  API_ACCESS_PATH,
  getNearestKeyExpiry,
  getUnrevokedKeys,
  getWorkingKeys,
  IntegrationCredentials,
  isExpiringSoon,
  showMissingRequirementError,
} from './integrationUtils';
import { formatDate } from '../../utils/date';
import { apiClientService } from '@api/services/services';
import { resources } from '@api/resources';
import { ApiScopeDto } from '@api/models/apiClient/ApiScopeDto';
import { ApiClientDetailDto } from '@api/models/apiClient/ApiClientDetailDto';
import { ApiClientStatus } from '@api/models/enums/ApiClientStatus';

const TAB_IDS = ['settings', 'keys', 'logs'] as const;
type TabId = (typeof TAB_IDS)[number];

interface Props {
  apiClientId: string;
  scopeCatalogue: ApiScopeDto[];
  onCredentialsIssued: (credentials: IntegrationCredentials) => void;
}

export const IntegrationOverview: FC<Props> = ({ apiClientId, scopeCatalogue, onCredentialsIssued }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ApiClientDetailDto>();
  //The tab is the last segment of the path (`.../keys` opens the keys right away, e.g. from the author of a change in a change history).
  const { tabId } = useParams();
  const tab = TAB_IDS.find((id) => id === tabId) ?? 'settings';
  //The status change the admin asked for, while the dialog is open; unsaved changes as they were at that moment.
  const [pendingSetActive, setPendingSetActive] = useState<{ active: boolean; hasUnsavedChanges: boolean } | null>(null);
  //The settings form stays mounted on the other tabs too, so its unsaved changes survive a tab switch.
  const formikRef = useRef<FormikProps<IntegrationFormValues>>(null);

  const loadDetail = useCallback(() => {
    apiClientService.getById(apiClientId).then((res) => setDetail(res.data));
  }, [apiClientId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleSubmit = async (values: IntegrationFormValues) => {
    const res = await apiClientService.update(apiClientId, toApiClientUpdateDto(values)).catch((error) => {
      showMissingRequirementError(t, error, resources.updateApiClient, scopeCatalogue);
      throw error;
    });
    setDetail(res.data);
  };

  const handleSetActive = async (active: boolean) => {
    await apiClientService.setActive(apiClientId, active);
    //Reloading resets the settings form, so unsaved changes were saved or discarded before.
    loadDetail();
  };

  const handleSetActiveClick = (active: boolean) => {
    const hasUnsavedChanges = !!formikRef.current?.dirty;
    if (active && !hasUnsavedChanges) {
      handleSetActive(true);
      return;
    }
    setPendingSetActive({ active, hasUnsavedChanges });
  };

  const handleSetActiveConfirm = async (active: boolean, saveChanges: boolean) => {
    const formik = formikRef.current;
    if (saveChanges && formik) {
      //Invalid values: submitting shows the errors in the form, and the status stays as it is.
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        formik.submitForm();
        return;
      }
      try {
        await handleSubmit(formik.values);
      } catch {
        //The save error is shown in the form; don't change the status without the changes.
        return;
      }
    }
    await handleSetActive(active);
  };

  const handleDelete = async () => {
    if (await apiClientService.deleteById(apiClientId)) navigate(API_ACCESS_PATH);
  };

  const isInactive = detail?.status === ApiClientStatus.Inactive;
  const unrevokedKeys = getUnrevokedKeys(detail?.keys ?? []);
  const hasOnlyExpiredKeys = unrevokedKeys.length > 0 && getWorkingKeys(unrevokedKeys).length === 0;
  const hasKeyExpiringSoon = unrevokedKeys.some((key) => isExpiringSoon(key.expiresAt));
  const hasKeyWithoutExpiry = unrevokedKeys.some((key) => !key.expiresAt);

  return (
    <>
      <IntegrationSetActiveDialog
        active={pendingSetActive?.active ?? null}
        hasUnsavedChanges={!!pendingSetActive?.hasUnsavedChanges}
        onClose={() => setPendingSetActive(null)}
        onConfirm={(saveChanges) => pendingSetActive && handleSetActiveConfirm(pendingSetActive.active, saveChanges)}
      />

      <DetailHeader
        backTo={API_ACCESS_PATH}
        titleContent={
          detail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0 }}>
              <QcsTypography variant="h4">{detail.name}</QcsTypography>
              <IntegrationStatusChip status={detail.status} />
            </Box>
          )
        }
      >
        {detail && (
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            {isInactive ? (
              <QcsButton variant="outlined" onClick={() => handleSetActiveClick(true)} resourceLoading={resources.setActiveApiClient}>
                {t('integration.apiAccess.activate')}
              </QcsButton>
            ) : (
              <QcsButton variant="outlined" onClick={() => handleSetActiveClick(false)} resourceLoading={resources.setActiveApiClient}>
                {t('integration.apiAccess.deactivate')}
              </QcsButton>
            )}
            <QcsButton variant="outlined" color="error" onClick={handleDelete} resourceLoading={resources.deleteApiClient}>
              {t('common.delete')}
            </QcsButton>
          </Box>
        )}
      </DetailHeader>

      <ShowResourceContent resource={[resources.detailApiClient, resources.apiClientScopes]}>
        {detail && (
          <>
            <ShowError resource={[resources.setActiveApiClient, resources.deleteApiClient]} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {detail.ipRanges.length === 0 && <QcsAlert severity="info">{t('integration.apiAccess.warnings.noIpRanges')}</QcsAlert>}
              {hasOnlyExpiredKeys && <QcsAlert severity="error">{t('integration.apiAccess.warnings.allKeysExpired')}</QcsAlert>}
              {hasKeyExpiringSoon && <QcsAlert severity="warning">{t('integration.apiAccess.warnings.keyExpiresSoon')}</QcsAlert>}
              {hasKeyWithoutExpiry && <QcsAlert severity="warning">{t('integration.apiAccess.warnings.keyWithoutExpiry')}</QcsAlert>}
            </Box>

            <InfoPaper sx={{ maxWidth: (theme) => theme.breakpoints.values.md, mb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '1rem' }}>
                <div>
                  <ReadOnlyField label={t('integration.apiAccess.authMethod.title')}>
                    {t(`integration.apiAccess.authMethod.${detail.authMethod}`)}
                  </ReadOnlyField>
                </div>
                {unrevokedKeys.length > 0 && (
                  <div>
                    <ReadOnlyField label={t('integration.apiAccess.key.expiresAt')}>
                      <QcsTypography variant="body2">
                        <IntegrationKeyExpiry expiresAt={getNearestKeyExpiry(detail.keys)} />
                      </QcsTypography>
                    </ReadOnlyField>
                  </div>
                )}
                <div>
                  <ReadOnlyField label={t('integration.apiAccess.created')}>
                    {`${formatDate(i18n, detail.createdAt, true)} ${detail.createdBy.name ?? ''}`.trim()}
                  </ReadOnlyField>
                </div>
                <div>
                  <ReadOnlyField label={t('integration.apiAccess.lastUsedAt')}>
                    {detail.lastUsedAt ? formatDate(i18n, detail.lastUsedAt, true) : t('integration.apiAccess.never')}
                  </ReadOnlyField>
                </div>
                <div>
                  <ReadOnlyField label={t('integration.apiAccess.lastError')}>
                    {detail.lastError
                      ? `${formatDate(i18n, detail.lastError.at, true)} - ${detail.lastError.code}`
                      : t('integration.apiAccess.noError')}
                  </ReadOnlyField>
                </div>
              </Box>
              <Box sx={{ mt: 2 }}>
                <ReadOnlyField label={t('integration.apiAccess.connection')}>
                  <IntegrationConnectionDetails details={detail} />
                </ReadOnlyField>
              </Box>
            </InfoPaper>

            <QcsTabs
              tabs={[
                { id: 'settings', label: t('integration.apiAccess.tabs.settings') },
                { id: 'keys', label: t('integration.apiAccess.tabs.keys') },
                { id: 'logs', label: t('integration.apiAccess.tabs.logs') },
              ]}
              value={tab}
              onChange={(_: SyntheticEvent, id: TabId) =>
                navigate(id === 'settings' ? `${API_ACCESS_PATH}/${apiClientId}` : `${API_ACCESS_PATH}/${apiClientId}/${id}`)
              }
            />

            <Box hidden={tab !== 'settings'}>
              <FormikDebounce<IntegrationFormValues>
                initialValues={toIntegrationFormValues(detail)}
                validationSchema={Yup.object().shape(integrationFormValidation(t, scopeCatalogue))}
                onSubmit={handleSubmit}
                enableReinitialize
                innerRef={formikRef}
              >
                <Form style={{ marginTop: '1rem' }}>
                  <FormContainer>
                    <IntegrationFormFields scopeCatalogue={scopeCatalogue} />
                    <ShowError resource={resources.updateApiClient} />
                    <IntegrationSubmitButton scopeCatalogue={scopeCatalogue} />
                  </FormContainer>
                </Form>
              </FormikDebounce>
            </Box>
            {tab === 'keys' && <IntegrationKeys detail={detail} onChanged={loadDetail} onCredentialsIssued={onCredentialsIssued} />}
            {tab === 'logs' && <IntegrationLogs apiClientId={apiClientId} />}
          </>
        )}
      </ShowResourceContent>
    </>
  );
};
